import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  Pagination,
  Tag,
  Button,
  Tooltip,
  message,
  Modal,
} from '@surecompanies/core_components'
import {
  CopyOutlined,
  WarningOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  RobotOutlined,
  StopOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import { isNil } from 'lodash'
import { getPrimaryPaymentMethodTagColor } from 'enrollment-platform/pages/finance/components/VcnManagement/VcnManagementTableCellTags'
import { ColDef } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import {
  PaymentAssignmentSortBy,
  PaymentAssignmentSortOrder,
  PaymentCase,
  PaginationState,
  Priority,
  PRIORITY_LABELS,
  isPaymentBotRunnable,
  isPaymentBotCancellable,
  PaymentAssignmentStatusId,
  PERSON_RELATIONSHIP_LABELS,
  PersonRelationshipEnum,
} from './Utils/types'
import { CurrencyFormatter } from 'helpers/formatters'
import { copyToClipboard } from 'components/CopyLink/CopyText'
import { PaymentStatusTag } from './PaymentStatusTag'
import {
  getPaymentTypeTagColor,
  getPriorityTagColor,
} from './payment-assignment.style'
import type { PaymentAssignmentRolePolicy } from './Utils/rolePolicy'
import ClearCost from './Utils/ClearCost'
import { getShortText, joinNameParts } from './Utils/payment-assignment.helper'
import { useColumnOrderPersistence } from './hooks/useColumnOrderPersistence'
import { useGridSortState } from './hooks/useGridSortState'

const TableContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #d6d8d9;
  border-radius: 4px;
  overflow-x: auto;
  overflow-y: visible;

  .ag-cell {
    display: flex;
    align-items: center;
  }
  position: relative;
  min-height: 200px;

  .ant-pagination {
    padding: 16px 24px;
    margin: 0;
  }
`

const FloatingButtonContainer = styled.div<{ $sidebarOffset: number }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #d6d8d9;
  border-radius: 8px;
  padding: 10px 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  white-space: nowrap;
`

const FloatingDivider = styled.div`
  width: 1px;
  height: 24px;
  background: #d6d8d9;
  flex-shrink: 0;
`

const SelectionLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #3a4951;
  padding-right: 4px;
`

const CopyableText = styled.span`
  cursor: pointer;
  color: #003146;

  &:hover {
    text-decoration: underline;
  }
`

const TerminatedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ff4d4f;
  color: #fff;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  margin-left: 5px;
  flex-shrink: 0;
`

const BinderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #2d5f9e;
  color: #fff;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  margin-left: 5px;
  flex-shrink: 0;
`

const currencyFormatter = new CurrencyFormatter()

interface PaymentAssignmentTableProps {
  cases: PaymentCase[]
  emptyText: React.ReactNode
  selectedRowKeys: string[]
  pagination: PaginationState
  sort?: {
    sortBy: PaymentAssignmentSortBy
    sortOrder: PaymentAssignmentSortOrder
  } | null
  onRowSelectionChange: (keys: string[]) => void
  onPaginationChange: (page: number, pageSize: number) => void
  onSortChange?: (
    sortBy: PaymentAssignmentSortBy | null,
    sortOrder: PaymentAssignmentSortOrder | null
  ) => void
  onRowClick: (paymentCase: PaymentCase) => void
  onAssigneeClick: (paymentCase: PaymentCase) => void
  onEscalatorClick?: (paymentCase: PaymentCase) => void
  onPriorityClick?: (paymentCase: PaymentCase) => void
  onBulkAssignClick?: () => void
  onBulkUnassignMakerClick?: () => void
  onBulkUnassignEscalatorClick?: () => void
  onBulkRunBotClick?: (selectedPaymentCases: PaymentCase[]) => void
  onRunBotClick: (paymentCase: PaymentCase) => void
  onBulkCancelBotClick?: (selectedPaymentCases: PaymentCase[]) => void
  onCancelBotClick?: (paymentCase: PaymentCase) => void
  rolePolicy: PaymentAssignmentRolePolicy
}

export default function PaymentAssignmentTable({
  cases,
  emptyText,
  selectedRowKeys,
  pagination,
  sort,
  onRowSelectionChange,
  onPaginationChange,
  onSortChange,
  onRowClick,
  onAssigneeClick,
  onEscalatorClick,
  onPriorityClick,
  onBulkAssignClick,
  onBulkUnassignMakerClick,
  onBulkUnassignEscalatorClick,
  onBulkRunBotClick,
  onRunBotClick,
  onBulkCancelBotClick,
  onCancelBotClick,
  rolePolicy,
}: PaymentAssignmentTableProps) {
  const [sidebarOffset, setSidebarOffset] = useState(0)
  const [terminatedWarningCase, setTerminatedWarningCase] =
    useState<PaymentCase | null>(null)
  const isMountedRef = useRef(true)
  const observerDebounceTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)

  useEffect(() => {
    isMountedRef.current = true

    // Check if sidebar is visible by looking for common sidebar selectors
    const checkSidebar = () => {
      if (!isMountedRef.current) return

      // Try to find the sidebar - check all aside elements first (most common)
      const allAsides = document.querySelectorAll('aside')
      let sidebar: Element | null = null
      let sidebarWidth = 0

      // Check all aside elements for visible sidebar on the left side
      for (const aside of Array.from(allAsides)) {
        const rect = aside.getBoundingClientRect()
        // Check if it's on the left side (left < 300px) and visible
        if (
          rect.left < 300 &&
          rect.width > 0 &&
          rect.height > 0 &&
          rect.left >= 0
        ) {
          sidebar = aside
          sidebarWidth = rect.width
          break
        }
      }

      // If no aside found, check navigation elements
      if (!sidebar) {
        const allNavs = document.querySelectorAll(
          'nav[role="navigation"], section[role="navigation"]'
        )
        for (const nav of Array.from(allNavs)) {
          const rect = nav.getBoundingClientRect()
          if (
            rect.left < 300 &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.left >= 0
          ) {
            sidebar = nav
            sidebarWidth = rect.width
            break
          }
        }
      }

      // If still no sidebar found, try class-based selectors
      if (!sidebar) {
        const possibleSidebars = document.querySelectorAll(
          '[class*="sidebar"], [class*="SideNav"], [class*="side-nav"]'
        )
        for (const el of Array.from(possibleSidebars)) {
          const rect = el.getBoundingClientRect()
          if (
            rect.left < 300 &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.left >= 0
          ) {
            sidebar = el
            sidebarWidth = rect.width
            break
          }
        }
      }

      // Calculate offset: if sidebar is visible, center button in content area
      // Content area starts after sidebar, so we offset by half the sidebar width
      if (sidebar && sidebarWidth > 0) {
        setSidebarOffset(sidebarWidth / 2)
      } else {
        setSidebarOffset(0)
      }
    }

    // Check on mount with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkSidebar, 100)

    // Check on window resize
    window.addEventListener('resize', checkSidebar)

    // Use MutationObserver to watch for DOM changes (sidebar show/hide)
    const observer = new MutationObserver(() => {
      // Debounce the check
      if (observerDebounceTimeoutRef.current) {
        clearTimeout(observerDebounceTimeoutRef.current)
      }
      observerDebounceTimeoutRef.current = setTimeout(checkSidebar, 100)
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    // Also check periodically as fallback
    const interval = setInterval(checkSidebar, 500)

    return () => {
      isMountedRef.current = false

      clearTimeout(timeoutId)
      if (observerDebounceTimeoutRef.current) {
        clearTimeout(observerDebounceTimeoutRef.current)
        observerDebounceTimeoutRef.current = null
      }
      window.removeEventListener('resize', checkSidebar)
      observer.disconnect()
      clearInterval(interval)
    }
  }, [])

  const handleCopy = useCallback((text: string) => {
    const trimmed = text?.trim()
    if (!trimmed) return

    void copyToClipboard(trimmed).then((ok) => {
      if (ok) message.success('Copied to clipboard')
    })
  }, [])

  const isTerminated = (paymentCase: PaymentCase) =>
    (paymentCase.insuredPerson?.employmentStatus?.toLowerCase() || '') ===
    'terminated'

  const handleRowClick = useCallback(
    (paymentCase: PaymentCase) => {
      if (isTerminated(paymentCase)) {
        setTerminatedWarningCase(paymentCase)
      } else {
        onRowClick(paymentCase)
      }
    },
    [onRowClick]
  )

  const renderAssignee = (paymentCase: PaymentCase) => {
    const isChild = isChildRow(paymentCase)
    const assigneeId = isNil(paymentCase.assigneeId)
      ? null
      : paymentCase.assigneeId

    if (isNil(assigneeId)) {
      if (!rolePolicy.canAssignMaker || isChild) {
        return '—'
      }
      return (
        <Button
          type="link"
          size="small"
          style={{ paddingLeft: 0 }}
          onClick={(e) => {
            e.stopPropagation()
            onAssigneeClick(paymentCase)
          }}
        >
          Assign
        </Button>
      )
    }

    if (!rolePolicy.canAssignMaker || isChild) {
      return <span>{paymentCase.assigneeName ?? '—'}</span>
    }

    return (
      <span
        style={{ cursor: 'pointer', color: '#003146' }}
        onClick={() => onAssigneeClick(paymentCase)}
      >
        {paymentCase.assigneeName}
      </span>
    )
  }

  const renderEscalator = (paymentCase: PaymentCase) => {
    const isChild = isChildRow(paymentCase)
    const hasEscalator =
      paymentCase.escalatorName != null && paymentCase.escalatorName !== ''
    if (!hasEscalator) {
      if (!rolePolicy.canAssignEscalator || isChild) {
        return '—'
      }
      const handleClick = onEscalatorClick ?? (() => onRowClick(paymentCase))
      return (
        <Button
          type="link"
          size="small"
          style={{ paddingLeft: 0 }}
          onClick={(e) => {
            e.stopPropagation()
            handleClick(paymentCase)
          }}
        >
          Assign
        </Button>
      )
    }
    if (!rolePolicy.canAssignEscalator || isChild) {
      return <span>{paymentCase.escalatorName}</span>
    }
    return (
      <span
        style={{ cursor: 'pointer', color: '#003146' }}
        onClick={() => onEscalatorClick?.(paymentCase)}
      >
        {paymentCase.escalatorName}
      </span>
    )
  }

  // Flatten cases to always include all children - parent first, then children below
  const flattenedCases = useMemo(() => {
    const result: PaymentCase[] = []
    for (const paymentCase of cases) {
      // Add the parent first
      result.push(paymentCase)
      // Then add children below (if they exist)
      if (
        paymentCase.migratedFromList &&
        paymentCase.migratedFromList.length > 0
      ) {
        for (const child of paymentCase.migratedFromList) {
          result.push(child)
        }
      }
    }
    return result
  }, [cases])

  const childBenefitIdSet = useMemo(() => {
    const set = new Set<string>()
    cases.forEach((parent) => {
      parent.migratedFromList?.forEach((child) => {
        if (child.benefitId) set.add(child.benefitId)
      })
    })
    return set
  }, [cases])

  const isChildRow = useCallback(
    (paymentCase: PaymentCase) =>
      !!paymentCase && childBenefitIdSet.has(paymentCase.benefitId),
    [childBenefitIdSet]
  )

  const defaultColDef = useMemo<ColDef<PaymentCase>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      cellStyle: (params) => {
        const isChild = isChildRow(params.data as PaymentCase)
        if (isChild) {
          return {
            color: '#999',
            fontSize: '12px',
          }
        }
        return undefined
      },
    }),
    [isChildRow]
  )

  const columnDefs = useMemo<ColDef<PaymentCase>[]>(() => {
    const defs: ColDef<PaymentCase>[] = [
      {
        headerName: '',
        colId: 'selection',
        width: 60,
        pinned: 'left',
        checkboxSelection: (params) => {
          // Only allow selection of parent rows, not children
          return !isChildRow(params.data as PaymentCase)
        },
        headerCheckboxSelection: true,
        suppressMovable: true,
        lockPosition: true,
        lockPinned: true,
        sortable: false,
      },
      {
        field: 'benefitId',
        headerName: 'Benefit ID',
        width: 150,
        colId: PaymentAssignmentSortBy.BENEFIT_ID,
        pinned: 'left',
        suppressMovable: true,
        lockPosition: true,
        lockPinned: true,
        cellRenderer: (params) => {
          const fullText = params.value ?? ''
          const isChild = isChildRow(params.data as PaymentCase)
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: isChild ? '#999' : 'inherit',
                fontSize: isChild ? '12px' : 'inherit',
              }}
            >
              {isChild && (
                <span
                  style={{
                    color: '#999',
                    fontSize: 14,
                    lineHeight: 1,
                  }}
                >
                  ↳
                </span>
              )}
              <Tooltip placement="topLeft" title={fullText}>
                {isChild ? (
                  <span>{getShortText(fullText, 3)}</span>
                ) : (
                  <CopyableText
                    data-copy-cell
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCopy(fullText)
                    }}
                  >
                    {getShortText(fullText, 3)}
                    <CopyOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                  </CopyableText>
                )}
              </Tooltip>
            </div>
          )
        },
      },
      {
        field: 'id',
        headerName: 'Case ID',
        width: 180,
        colId: PaymentAssignmentSortBy.ID,
        cellRenderer: (params) => {
          const paymentCase = params.data as PaymentCase
          const fullText = (params.value ?? '').toString()
          const terminated = isTerminated(paymentCase)
          const isBinder =
            paymentCase?.needsBinderPayment === true ||
            paymentCase?.isBinderPayment === true
          const isChild = isChildRow(paymentCase)

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: isChild ? '#999' : 'inherit',
                fontSize: isChild ? '12px' : 'inherit',
              }}
            >
              <Tooltip placement="topLeft" title={fullText}>
                {isChild ? (
                  <span>{fullText}</span>
                ) : (
                  <CopyableText
                    data-copy-cell
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCopy(fullText)
                    }}
                  >
                    {fullText}
                    <CopyOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                  </CopyableText>
                )}
              </Tooltip>
              {terminated && !isChild && (
                <Tooltip placement="top" title="Employee is Terminated">
                  <TerminatedBadge>T</TerminatedBadge>
                </Tooltip>
              )}
              {isBinder && !isChild && (
                <Tooltip
                  placement="top"
                  title={
                    paymentCase?.needsBinderPayment
                      ? 'Needs Binder Payment'
                      : 'Binder Payment'
                  }
                >
                  <BinderBadge>B</BinderBadge>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        headerName: 'Carrier',
        width: 120,
        colId: PaymentAssignmentSortBy.CARRIER_NAME,
        sortable: true,
        valueGetter: (params) =>
          params.data?.paymentCarrier?.name ??
          params.data?.benefit?.carrierName ??
          '—',
        cellRenderer: (params) => {
          const carrierName =
            params.data?.paymentCarrier?.name ??
            params.data?.benefit?.carrierName ??
            '—'
          return (
            <Tooltip placement="topLeft" title={carrierName}>
              <span>{carrierName}</span>
            </Tooltip>
          )
        },
      },
      {
        field: 'groupId',
        headerName: 'Group ID',
        width: 150,
        colId: PaymentAssignmentSortBy.GROUP_ID,
        sortable: true,
        valueGetter: (params) => params.data?.groupId ?? '',
        cellRenderer: (params) => {
          const fullText = params.value ?? ''
          const displayText = fullText || '—'
          return (
            <Tooltip placement="topLeft" title={fullText || undefined}>
              <CopyableText
                data-copy-cell
                onClick={(e) => {
                  e.stopPropagation()
                  handleCopy(fullText)
                }}
              >
                {displayText}{' '}
                {fullText ? (
                  <CopyOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                ) : null}
              </CopyableText>
            </Tooltip>
          )
        },
      },
      {
        field: 'companyEmployeeId',
        headerName: 'Company EE ID',
        width: 150,
        colId: PaymentAssignmentSortBy.COMPANY_EMPLOYEE_ID,
        valueGetter: (params) => params.data?.companyEmployeeId ?? '',
        cellRenderer: (params) => {
          const fullText = params.value ?? ''
          const displayText = fullText || '—'
          const isChild = isChildRow(params.data as PaymentCase)
          return (
            <Tooltip placement="topLeft" title={fullText || undefined}>
              {isChild ? (
                <span>{displayText}</span>
              ) : (
                <CopyableText
                  data-copy-cell
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCopy(fullText)
                  }}
                >
                  {displayText}{' '}
                  {fullText ? (
                    <CopyOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                  ) : null}
                </CopyableText>
              )}
            </Tooltip>
          )
        },
      },
      {
        headerName: 'Company Name',
        width: 180,
        colId: PaymentAssignmentSortBy.COMPANY_NAME,
        valueGetter: (params) =>
          params.data?.companyName ?? params.data?.benefit?.companyName ?? '—',
        cellRenderer: (params) => {
          const name = params.value ?? '—'
          return (
            <Tooltip placement="topLeft" title={name === '—' ? '' : name}>
              {name}
            </Tooltip>
          )
        },
      },
      {
        headerName: 'Insured Person Name',
        width: 180,
        colId: PaymentAssignmentSortBy.INSURED_PERSON_NAME,
        sortable: true,
        cellRenderer: (params) => {
          const record = params.data
          const name =
            record?.insuredPerson?.firstName != null
              ? joinNameParts(
                  record.insuredPerson.firstName,
                  record.insuredPerson.middleName,
                  record.insuredPerson.lastName
                )
              : '—'
          return (
            <span
              style={{ cursor: 'pointer', color: '#003146' }}
              onClick={(e) => {
                e.stopPropagation()
                if (record) handleRowClick(record)
              }}
            >
              {name}
            </span>
          )
        },
      },
      {
        headerName: 'Relation',
        width: 160,
        colId: PaymentAssignmentSortBy.INSURED_PERSON_RELATIONSHIP,
        sortable: true,
        valueGetter: (params) =>
          params.data?.insuredPerson?.relationship ?? '—',
        cellRenderer: (params) => {
          const rel = params.data?.insuredPerson?.relationship as
            | PersonRelationshipEnum
            | undefined
          const label = rel ? (PERSON_RELATIONSHIP_LABELS[rel] ?? rel) : '—'
          return (
            <Tooltip placement="topLeft" title={label === '—' ? '' : label}>
              <span>{label}</span>
            </Tooltip>
          )
        },
      },
      {
        headerName: 'Benefit Effective Date',
        width: 120,
        colId: PaymentAssignmentSortBy.BENEFIT_EFFECTIVE_DATE,
        valueGetter: (params) => {
          const row = params.data
          return (
            row?.benefitEffectiveDate ??
            row?.benefit?.benefitEffectiveDate ??
            '—'
          )
        },
      },
      {
        headerName: 'Benefit End Date',
        width: 120,
        colId: PaymentAssignmentSortBy.BENEFIT_END_DATE,
        valueGetter: (params) => {
          const row = params.data
          return row?.benefitEndDate ?? row?.benefit?.benefitEndDate ?? '—'
        },
      },
      {
        headerName: 'Plan Cost',
        width: 120,
        colId: PaymentAssignmentSortBy.CURRENT_EXPECTED_COST,
        valueGetter: (params) => {
          const r = params.data
          const cost = r?.[PaymentAssignmentSortBy.CURRENT_EXPECTED_COST] ?? 0
          const numericCost = Number(isNil(cost) ? 0 : cost)
          return Number.isFinite(numericCost) ? numericCost : 0
        },
        valueFormatter: (params) => {
          const numericCost = Number(params.value ?? 0)
          return currencyFormatter.format(
            Number.isFinite(numericCost) ? numericCost : 0
          )
        },
        cellStyle: { fontWeight: 700 },
      },
      {
        headerName: 'Cleared Cost',
        width: 120,
        colId: PaymentAssignmentSortBy.CLEARED_COST,
        cellRenderer: (params) => {
          const r = params.data
          const clearedCost = r?.[PaymentAssignmentSortBy.CLEARED_COST] ?? 0
          const currentExpectedCost =
            r?.[PaymentAssignmentSortBy.CURRENT_EXPECTED_COST] ?? 0
          return (
            <ClearCost
              clearedCost={Number(clearedCost)}
              planCost={Number(currentExpectedCost)}
            />
          )
        },
        cellStyle: { fontWeight: 750 },
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 160,
        colId: PaymentAssignmentSortBy.PRIORITY,
        sortable: true,
        cellRenderer: (params) => {
          const paymentCase = params.data as PaymentCase | undefined
          if (!paymentCase) return null
          const priority = params.value ?? Priority.DEFAULT
          const isChild = isChildRow(paymentCase)
          const canAssign = rolePolicy.canAssignPriority && !isChild
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            if (canAssign) onPriorityClick?.(paymentCase)
          }

          if (priority === Priority.DEFAULT) {
            if (!canAssign) return '-'
            return (
              <Button
                type="link"
                size="small"
                style={{ paddingLeft: 0 }}
                onClick={handleClick}
              >
                Assign
              </Button>
            )
          }

          const label = PRIORITY_LABELS[priority]
          if (!canAssign) {
            return <Tag color={getPriorityTagColor(priority)}>{label}</Tag>
          }
          return (
            <span style={{ cursor: 'pointer' }} onClick={handleClick}>
              <Tag color={getPriorityTagColor(priority)}>{label}</Tag>
            </span>
          )
        },
      },
      {
        field: 'paymentType',
        headerName: 'Payment Type',
        width: 150,
        colId: PaymentAssignmentSortBy.PAYMENT_TYPE,
        cellRenderer: (params) => (
          <span onClick={(e) => e.stopPropagation()}>
            <Tag color={getPaymentTypeTagColor(String(params.value ?? ''))}>
              {isNil(params.value) ? '—' : params.value}
            </Tag>
          </span>
        ),
      },
      {
        field: 'primaryPaymentMethod',
        headerName: 'Primary Payment Method',
        width: 190,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: (params) => {
          const raw = String(params.value ?? '').trim()
          if (!raw) return <span style={{ color: '#8c8c8c' }}>—</span>
          return (
            <span onClick={(e) => e.stopPropagation()}>
              <Tag color={getPrimaryPaymentMethodTagColor(raw)}>{raw}</Tag>
            </span>
          )
        },
      },
      {
        headerName: 'Maker',
        width: 150,
        colId: PaymentAssignmentSortBy.ASSIGNEE_ID,
        sortable: true,
        cellRenderer: (params) => params.data && renderAssignee(params.data),
      },
      {
        headerName: 'Escalator',
        width: 150,
        colId: PaymentAssignmentSortBy.ESCALATOR_ID,
        sortable: true,
        cellRenderer: (params) => params.data && renderEscalator(params.data),
      },
      {
        headerName: 'Status',
        width: 160,
        colId: PaymentAssignmentSortBy.STATUS_ID,
        autoHeight: true,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'normal',
          lineHeight: '1.4',
          paddingTop: 6,
          paddingBottom: 6,
        },
        cellRenderer: (params) =>
          params.data ? (
            <span onClick={(e) => e.stopPropagation()}>
              <PaymentStatusTag
                onClick={() => onRowClick(params.data)}
                statusId={
                  params.data.paymentStatus?.id as PaymentAssignmentStatusId
                }
              />
            </span>
          ) : null,
      },
      {
        field: 'paymentSubmittedBy',
        headerName: 'Payment submitted by',
        width: 170,
        colId: 'paymentSubmittedBy',
        sortable: false,
        valueGetter: (params) => params.data?.paymentSubmittedBy?.trim() ?? '',
        cellRenderer: (params) => {
          const text = (params.value as string)?.trim() || '—'
          return (
            <Tooltip placement="topLeft" title={text === '—' ? '' : text}>
              <span onClick={(e) => e.stopPropagation()}>{text}</span>
            </Tooltip>
          )
        },
      },
      {
        headerName: 'Escalation Reason',
        width: 180,
        colId: PaymentAssignmentSortBy.ESCALATION_REASON,
        sortable: true,
        cellRenderer: (params) => (
          <span onClick={(e) => e.stopPropagation()}>
            {params.data?.escalationReason || ''}
          </span>
        ),
      },
      {
        headerName: 'Ticket',
        width: 180,
        colId: PaymentAssignmentSortBy.TICKET,
        sortable: true,
        cellRenderer: (params) => (
          <span onClick={(e) => e.stopPropagation()}>
            {params.data?.ticket || ''}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        width: 140,
        colId: 'actions',
        sortable: false,
        cellStyle: {
          gap: '2px',
          paddingTop: '6px',
          paddingBottom: '6px',
        },
        cellRenderer: (params) => {
          if (!params.data) return null
          const paymentCase = params.data
          if (isChildRow(paymentCase)) return null
          const showCancel =
            onCancelBotClick != null &&
            rolePolicy.canCancelPayment &&
            isPaymentBotCancellable(paymentCase)
          const showSubmit = !showCancel && rolePolicy.canSubmitPayment
          const canSubmit = showSubmit && isPaymentBotRunnable(paymentCase)
          if (!showCancel && !showSubmit) return null
          const linkButtonStyle = {
            padding: '0 4px',
            height: 'auto',
            fontSize: 12,
          } as const
          return (
            <span onClick={(e) => e.stopPropagation()}>
              {showCancel ? (
                <Button
                  type="link"
                  size="small"
                  danger
                  style={linkButtonStyle}
                  onClick={(e) => {
                    e.stopPropagation()
                    onCancelBotClick(paymentCase)
                  }}
                  aria-label="Cancel bot payment"
                >
                  Cancel bot payment
                </Button>
              ) : (
                <Button
                  type="link"
                  size="small"
                  disabled={!canSubmit}
                  style={linkButtonStyle}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (canSubmit) onRunBotClick(paymentCase)
                  }}
                  aria-label="Submit payment"
                >
                  Submit payment
                </Button>
              )}
            </span>
          )
        },
      },
    ]
    return defs
      .filter(
        (col) =>
          rolePolicy.showEscalatorColumn ||
          col.colId !== PaymentAssignmentSortBy.ESCALATOR_ID
      )
      .filter(
        (col) =>
          rolePolicy.showMakerColumn ||
          col.colId !== PaymentAssignmentSortBy.ASSIGNEE_ID
      )
      .filter(
        (col) =>
          rolePolicy.showTicketColumn ||
          col.colId !== PaymentAssignmentSortBy.TICKET
      )
  }, [
    handleCopy,
    handleRowClick,
    onAssigneeClick,
    onEscalatorClick,
    onPriorityClick,
    onRowClick,
    onRunBotClick,
    onCancelBotClick,
    rolePolicy.canAssignEscalator,
    rolePolicy.canCancelPayment,
    rolePolicy.canAssignMaker,
    rolePolicy.canAssignPriority,
    rolePolicy.canSubmitPayment,
    rolePolicy.showEscalatorColumn,
    rolePolicy.showMakerColumn,
    rolePolicy.showTicketColumn,
    isChildRow,
  ])

  const gridRef = useRef<AgGridReact<PaymentCase>>(null)

  const { applySortState, onSortChanged } = useGridSortState<PaymentCase>(
    gridRef,
    sort,
    (sortBy, sortOrder) =>
      onSortChange?.(
        sortBy as PaymentAssignmentSortBy | null,
        sortOrder as PaymentAssignmentSortOrder | null
      )
  )

  const hasSameSelection = useCallback(
    (currentSelection: string[], nextSelection: string[]) => {
      if (currentSelection.length !== nextSelection.length) return false

      const currentSelectionSet = new Set(currentSelection)
      if (currentSelectionSet.size !== currentSelection.length) return false

      return nextSelection.every((key) => currentSelectionSet.has(key))
    },
    []
  )

  useEffect(() => {
    if (!gridRef.current?.api) return
    const selectedRowKeySet = new Set(selectedRowKeys)

    gridRef.current.api.forEachNode((node) => {
      const benefitId = node.data?.benefitId ?? ''
      // Don't select child rows - only parent rows can be selected
      const isChild = isChildRow(node.data as PaymentCase)
      const shouldBeSelected = !isChild && selectedRowKeySet.has(benefitId)

      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected, false)
      }
    })
  }, [selectedRowKeys, flattenedCases, isChildRow])

  const handleSelectionChanged = useCallback(() => {
    const selected = gridRef.current?.api?.getSelectedRows() ?? []
    const nextSelectedRowKeys = selected
      .filter((row) => !isChildRow(row))
      .map((row) => row.benefitId)

    if (hasSameSelection(selectedRowKeys, nextSelectedRowKeys)) return

    onRowSelectionChange(nextSelectedRowKeys)
  }, [hasSameSelection, isChildRow, onRowSelectionChange, selectedRowKeys])

  const isRowSelectable = useCallback(
    (node: { data?: PaymentCase }) => !isChildRow(node.data as PaymentCase),
    [isChildRow]
  )

  const { restoreColumnOrder, handleColumnMoved } = useColumnOrderPersistence(
    gridRef,
    'paymentAssignmentTable:columnOrder:v2'
  )

  const onGridReady = useCallback(() => {
    restoreColumnOrder()
    applySortState()
  }, [restoreColumnOrder, applySortState])

  const canAssignAction =
    !!onBulkAssignClick && rolePolicy.canBulkAssign && rolePolicy.canAssignMaker

  const botSelectedCount = useMemo(() => {
    const runnableBenefitIds = new Set(
      cases.filter(isPaymentBotRunnable).map((c) => c.benefitId)
    )
    return selectedRowKeys.filter((key) => runnableBenefitIds.has(key)).length
  }, [selectedRowKeys, cases])
  const canBotAction = rolePolicy.canSubmitPayment
  const botCancellableCount = useMemo(() => {
    const cancellableBenefitIds = new Set(
      cases.filter(isPaymentBotCancellable).map((c) => c.benefitId)
    )
    return selectedRowKeys.filter((key) => cancellableBenefitIds.has(key))
      .length
  }, [selectedRowKeys, cases])
  const canCancelBotAction = rolePolicy.canCancelPayment

  const makerAssignedCount = useMemo(() => {
    const selectedKeySet = new Set(selectedRowKeys)
    return cases.filter(
      (c) =>
        selectedKeySet.has(c.benefitId) &&
        !isNil(c.assigneeId) &&
        c.assigneeId !== 0
    ).length
  }, [selectedRowKeys, cases])

  const escalatorAssignedCount = useMemo(() => {
    const selectedKeySet = new Set(selectedRowKeys)
    return cases.filter(
      (c) =>
        selectedKeySet.has(c.benefitId) &&
        !isNil(c.escalatorId) &&
        c.escalatorId !== 0
    ).length
  }, [selectedRowKeys, cases])

  const canUnassignMaker =
    !!onBulkUnassignMakerClick &&
    rolePolicy.canBulkAssign &&
    rolePolicy.canAssignMaker

  const canUnassignEscalator =
    !!onBulkUnassignEscalatorClick &&
    rolePolicy.canBulkAssign &&
    rolePolicy.canAssignEscalator

  const showFloatingActions = selectedRowKeys.length > 0

  const isShowFloatingActions =
    canAssignAction ||
    canUnassignMaker ||
    canUnassignEscalator ||
    canBotAction ||
    canCancelBotAction

  return (
    <TableContainer>
      <div
        className="ag-theme-alpine"
        style={{
          width: '100%',
          height: 'min(680px, calc(100vh - 280px))',
          minHeight: 400,
        }}
      >
        <AgGridReact<PaymentCase>
          ref={gridRef}
          rowData={flattenedCases}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data?.benefitId ?? ''}
          rowSelection="multiple"
          isRowSelectable={isRowSelectable}
          onSelectionChanged={handleSelectionChanged}
          onSortChanged={onSortChanged}
          postSortRows={(params) => {
            // Keep child rows directly below their parent regardless of sort
            const rowNodes = params.nodes
            const childBenefitIds = new Set<string>()
            const childrenByParent = new Map<string, typeof rowNodes>()

            for (const parent of cases) {
              if (
                parent.migratedFromList &&
                parent.migratedFromList.length > 0
              ) {
                for (const child of parent.migratedFromList) {
                  childBenefitIds.add(child.benefitId)
                }
              }
            }

            // Collect child nodes keyed by their parent benefitId
            for (const node of rowNodes) {
              const benefitId = node.data?.benefitId ?? ''
              if (childBenefitIds.has(benefitId)) {
                const parent = cases.find((p) =>
                  p.migratedFromList?.some((c) => c.benefitId === benefitId)
                )
                if (parent) {
                  const list = childrenByParent.get(parent.benefitId) ?? []
                  list.push(node)
                  childrenByParent.set(parent.benefitId, list)
                }
              }
            }

            // Rebuild the row order: each parent followed by its children
            const reordered: typeof rowNodes = []
            for (const node of rowNodes) {
              const benefitId = node.data?.benefitId ?? ''
              if (childBenefitIds.has(benefitId)) continue
              reordered.push(node)
              const children = childrenByParent.get(benefitId)
              if (children) {
                reordered.push(...children)
              }
            }

            rowNodes.length = 0
            rowNodes.push(...reordered)
          }}
          getRowStyle={(params) => {
            const c = params.data as PaymentCase
            const isChild = isChildRow(c)
            if (isTerminated(c)) {
              return { backgroundColor: '#fff1f0' }
            }
            if (c?.needsBinderPayment === true || c?.isBinderPayment === true) {
              return { backgroundColor: '#eef3fb' }
            }
            if (isChild) {
              return { backgroundColor: '#f5f5f5' }
            }
            return undefined
          }}
          onCellClicked={(event) => {
            const skipColumns: Set<string> = new Set([
              PaymentAssignmentSortBy.ASSIGNEE_ID,
              PaymentAssignmentSortBy.ESCALATOR_ID,
              PaymentAssignmentSortBy.PRIORITY,
              PaymentAssignmentSortBy.STATUS_ID,
              'actions',
            ])
            if (
              event.colDef.checkboxSelection ||
              skipColumns.has(event.column.getColId())
            )
              return
            const target = event.event?.target as HTMLElement | undefined
            if (target?.closest?.('[data-copy-cell]')) return
            if (event.data) handleRowClick(event.data)
          }}
          maintainColumnOrder
          onGridReady={onGridReady}
          onColumnMoved={handleColumnMoved}
          suppressRowClickSelection
          suppressCellFocus
          rowStyle={{ cursor: 'pointer' }}
          noRowsOverlayComponent={() => (
            <div className="ag-overlay-no-rows-center" style={{ padding: 16 }}>
              {emptyText}
            </div>
          )}
        />
      </div>
      {pagination.total > 0 && (
        <div
          className="ant-pagination-wrapper"
          style={{ padding: '16px 24px', margin: 0 }}
        >
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.pageSize}
            pageSizeOptions={[25, 50, 100, 200]}
            showSizeChanger
            showTotal={(total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} cases`
            }
            onChange={onPaginationChange}
          />
        </div>
      )}
      <Modal
        open={terminatedWarningCase !== null}
        title={
          <span>
            <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            Terminated Employee
          </span>
        }
        onCancel={() => setTerminatedWarningCase(null)}
        footer={[
          <Button key="cancel" onClick={() => setTerminatedWarningCase(null)}>
            Cancel
          </Button>,
          <Button
            key="proceed"
            type="primary"
            danger
            onClick={() => {
              const c = terminatedWarningCase
              setTerminatedWarningCase(null)
              if (c) onRowClick(c)
            }}
          >
            View Anyway
          </Button>,
        ]}
      >
        <p>
          This insured person&apos;s employment status is{' '}
          <strong>Terminated</strong>. Please review carefully before making a
          payment.
        </p>
        {terminatedWarningCase?.insuredPerson && (
          <p style={{ margin: 0, color: '#595959' }}>
            <strong>
              {terminatedWarningCase.insuredPerson.firstName}{' '}
              {terminatedWarningCase.insuredPerson.lastName}
            </strong>{' '}
            &mdash; Employment Status:{' '}
            <Tag color="red">
              {terminatedWarningCase.insuredPerson.employmentStatus}
            </Tag>
          </p>
        )}
      </Modal>

      {showFloatingActions && (
        <FloatingButtonContainer $sidebarOffset={sidebarOffset}>
          <SelectionLabel>{selectedRowKeys.length} selected</SelectionLabel>

          {isShowFloatingActions && <FloatingDivider />}

          {canAssignAction && onBulkAssignClick && (
            <Button
              size="middle"
              type="primary"
              icon={<UserAddOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                onBulkAssignClick()
              }}
            >
              Bulk Assign ({selectedRowKeys.length})
            </Button>
          )}

          {canUnassignMaker && onBulkUnassignMakerClick && (
            <Button
              size="middle"
              icon={<UserDeleteOutlined />}
              disabled={makerAssignedCount === 0}
              onClick={(e) => {
                e.stopPropagation()
                onBulkUnassignMakerClick()
              }}
            >
              Unassign Maker ({makerAssignedCount})
            </Button>
          )}

          {canUnassignEscalator && onBulkUnassignEscalatorClick && (
            <Button
              size="middle"
              icon={<UserDeleteOutlined />}
              disabled={escalatorAssignedCount === 0}
              onClick={(e) => {
                e.stopPropagation()
                onBulkUnassignEscalatorClick()
              }}
            >
              Unassign Escalator ({escalatorAssignedCount})
            </Button>
          )}

          {selectedRowKeys.length === 1 ? (
            <>
              {onRunBotClick && canBotAction && (
                <Button
                  size="middle"
                  type="default"
                  icon={<RobotOutlined />}
                  disabled={botSelectedCount === 0}
                  onClick={(e) => {
                    e.stopPropagation()
                    const selectedKey = selectedRowKeys?.[0]
                    if (!selectedKey) return
                    const selectedCase = cases.find(
                      (c) => c.benefitId === selectedKey
                    )
                    if (selectedCase) onRunBotClick(selectedCase)
                  }}
                >
                  Submit bot payment
                </Button>
              )}
              {onCancelBotClick && canCancelBotAction && (
                <Button
                  size="middle"
                  danger
                  icon={<StopOutlined />}
                  disabled={botCancellableCount === 0}
                  onClick={(e) => {
                    e.stopPropagation()
                    const selectedKey = selectedRowKeys?.[0]
                    if (!selectedKey) return
                    const selectedCase = cases.find(
                      (c) => c.benefitId === selectedKey
                    )
                    if (selectedCase) onCancelBotClick(selectedCase)
                  }}
                >
                  Cancel bot payment
                </Button>
              )}
            </>
          ) : (
            <>
              {onBulkRunBotClick && canBotAction && (
                <Button
                  size="middle"
                  type="default"
                  icon={<RobotOutlined />}
                  disabled={botSelectedCount === 0}
                  style={
                    botSelectedCount > 0
                      ? {
                          color: 'purple',
                          borderColor: 'purple',
                          backgroundColor: '#f3e6fa',
                        }
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    const selectedPaymentCases = cases.filter(
                      (c) =>
                        selectedRowKeys.includes(c.benefitId) &&
                        isPaymentBotRunnable(c)
                    )
                    onBulkRunBotClick(selectedPaymentCases)
                  }}
                >
                  Submit bot payment ({botSelectedCount})
                </Button>
              )}
              {onBulkCancelBotClick && canCancelBotAction && (
                <Button
                  size="middle"
                  danger
                  icon={<StopOutlined />}
                  disabled={botCancellableCount === 0}
                  onClick={(e) => {
                    e.stopPropagation()
                    const selectedPaymentCases = cases.filter(
                      (c) =>
                        selectedRowKeys.includes(c.benefitId) &&
                        isPaymentBotCancellable(c)
                    )
                    onBulkCancelBotClick(selectedPaymentCases)
                  }}
                >
                  Cancel bot payment ({botCancellableCount})
                </Button>
              )}
            </>
          )}

          <FloatingDivider />
          <Button
            size="middle"
            type="text"
            icon={<CloseOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onRowSelectionChange([])
            }}
            style={{ color: '#677786', fontWeight: 500 }}
          >
            Exit
          </Button>
        </FloatingButtonContainer>
      )}
    </TableContainer>
  )
}
